import PropTypes from 'prop-types';
import React, { useCallback, useState, useRef } from 'react';
import useOnClickOutside from 'use-onclickoutside';
import { useMedia } from 'react-use';
import { addDays, isBefore, format, startOfDay } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-regular-svg-icons';
import { faCaretDown, faCaretUp, faTimes } from '@fortawesome/free-solid-svg-icons';
import { SEARCH_FORMAT_DATE } from '@ts/utils/constants';
import { AnalyticsInstance } from '@ts/utils/analytics';
import ControlledDayPicker from '../../ControlledDayPicker';

import * as S from './styles';
import classes from './DateRangeSelector.module.css';

function DateRangeSelector({ dateFrom, dateTo, onSelect }) {
    const [isOpen, changeIsOpen] = useState(false);

    const innerRef = useRef(null);
    const monthRef = useRef(undefined);
    const isMobile = useMedia('(max-width: 640px)', true);

    const now = startOfDay(new Date());
    const _startDate = dateFrom || now;
    const _endDate = dateTo || now;

    const startDate = isBefore(_startDate, now) ? now : _startDate;
    const endDate = isBefore(_endDate, startDate) ? startDate : _endDate;

    const isSelected = Boolean(dateFrom && dateTo);
    const numberOfMonths = isMobile ? 1 : 2;

    const onMonthChange = useCallback((date) => (monthRef.current = date), [monthRef]);
    const close = useCallback(() => changeIsOpen(false), [changeIsOpen]);
    const toggle = useCallback(() => changeIsOpen(!isOpen), [changeIsOpen, isOpen]);

    const onClickReset = useCallback(
        (evt) => {
            evt.stopPropagation();
            if (typeof onSelect === 'function') {
                onSelect(null, null);
            }
        },
        [onSelect]
    );

    const onDatesChange = useCallback(
        (dates) => {
            try {
                const start = dates.startDate;
                const end = dates.endDate;

                if (typeof onSelect !== 'function') {
                    return;
                }

                const fStartDate = start ? start : null;
                const fEndDate = end ? end : fStartDate;
                onSelect(fStartDate, fEndDate);
                changeIsOpen(false);

                AnalyticsInstance.navigation.selectedSearchDates({
                    startDate: start,
                    endDate: end,
                });
            } catch (err) {
                console.log(err);
            }
        },
        [onSelect]
    );

    const onClickSelectToday = useCallback(() => {
        const date = new Date();
        onDatesChange({
            startDate: date,
            endDate: date,
        });
        AnalyticsInstance.navigation.selectedSearchDates({ startDate: date });
    }, [onDatesChange]);

    const onClickSelectTomorrow = useCallback(() => {
        const date = addDays(new Date(), 1);
        onDatesChange({
            startDate: date,
            endDate: date,
        });
        AnalyticsInstance.navigation.selectedSearchDates({ startDate: date });
    }, [onDatesChange]);

    useOnClickOutside(innerRef, close);

    return (
        <S.DateRangeSelector
            data-test-id="date-range-selector"
            className="header__dateRangeSelector"
            ref={innerRef}
        >
            <S.Selector
                data-test-id="selector"
                className={isSelected ? 'selected' : ''}
                onClick={toggle}
            >
                <S.SelectorIcon data-test-id="icon">
                    <FontAwesomeIcon icon={faCalendarAlt} />
                </S.SelectorIcon>
                <S.SelectorSelected data-test-id="selected">
                    {isSelected && (
                        <span>
                            <span>{format(startDate, SEARCH_FORMAT_DATE)}</span>
                            <span> ~ </span>
                            <span>{format(endDate, SEARCH_FORMAT_DATE)}</span>
                        </span>
                    )}
                    {!isSelected && <span>Please choose date</span>}
                </S.SelectorSelected>
                <S.SelectorDropdownIndicator>
                    <React.Fragment>
                        {isSelected && isBefore(now, startDate) && (
                            <S.SelectorDropdownIndicatorReset
                                data-test-id="times"
                                onClick={onClickReset}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </S.SelectorDropdownIndicatorReset>
                        )}
                    </React.Fragment>
                    <React.Fragment>
                        {!isOpen && <FontAwesomeIcon data-test-id="caret" icon={faCaretDown} />}
                    </React.Fragment>
                    <React.Fragment>
                        {isOpen && <FontAwesomeIcon data-test-id="caret" icon={faCaretUp} />}
                    </React.Fragment>
                </S.SelectorDropdownIndicator>
            </S.Selector>

            <S.Animated
                style={{
                    ...(isOpen
                        ? { display: 'block', opacity: 1, top: 50 }
                        : { display: 'none', opacity: 0, top: 30 }),
                    ...(isOpen ? {} : { pointerEvents: 'none' }),
                }}
            >
                <ControlledDayPicker
                    startDate={startDate}
                    endDate={endDate}
                    numberOfMonths={numberOfMonths}
                    onDatesChange={onDatesChange}
                    initialMonth={monthRef.current}
                    onMonthChange={onMonthChange}
                />
                <div data-test-id="info-panel" className={classes.calendarInfoPanel}>
                    <div data-test-id="today" className={classes.calendarInfoPanelColumn}>
                        <button
                            type="button"
                            onClick={onClickSelectToday}
                            className={classes.calendarInfoPanelColumnButton}
                        >
                            #today
                        </button>
                    </div>
                    <div data-test-id="tomorrow" className={classes.calendarInfoPanelColumn}>
                        <button
                            type="button"
                            onClick={onClickSelectTomorrow}
                            className={classes.calendarInfoPanelColumnButton}
                        >
                            #tomorrow
                        </button>
                    </div>
                </div>
            </S.Animated>
        </S.DateRangeSelector>
    );
}

DateRangeSelector.propTypes = {
    onSelect: PropTypes.func.isRequired,
    dateFrom: PropTypes.instanceOf(Date),
    dateTo: PropTypes.instanceOf(Date),
};

export default React.memo(DateRangeSelector);
